import {
  ROLE_NAMES,
  SITE_BOUND_ROLES,
  creatableRoles,
  type EmployeeRow,
  type Role,
} from '@pms/shared';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuthStore, useCan } from '../../auth/store.js';
import { request } from '../../sync/api.js';

/**
 * Сотрудники: кто работает, чем входит, к каким участкам допущен, кто уволен.
 *
 * Увольнение — не удаление. Смены, отчёты и выработка остаются за человеком,
 * он уходит в архив, откуда его можно вернуть, если выйдет снова.
 *
 * Экран работает только при сети — в отличие от смен, которые копятся в очереди.
 * Так сделано намеренно: пароль хэшируется на сервере, а очередь событий
 * рассчитана на то, что происходит в цехе, а не на смену прав доступа.
 */

interface Site {
  id: string;
  code: string;
  name: string;
}

interface StaffResponse {
  employees: EmployeeRow[];
  sites: Site[];
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

const EMPTY_FORM = {
  lastName: '',
  firstName: '',
  middleName: '',
  phone: '',
  role: 'OPERATOR' as Role,
  login: '',
  password: '',
  hiredAt: today(),
  siteIds: [] as string[],
};

export function EmployeesScreen(): JSX.Element {
  const [data, setData] = useState<StaffResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingSites, setEditingSites] = useState<string | null>(null);
  const [draftSites, setDraftSites] = useState<string[]>([]);
  /**
   * Правка имени. Нужна прежде всего для тех, кого оформили из найма: там имя
   * приходит одной строкой с площадки вакансий, и на части его делит догадка
   * по пробелам — она ошибается.
   */
  const [editingName, setEditingName] = useState<string | null>(null);
  const [draftName, setDraftName] = useState({ lastName: '', firstName: '', middleName: '' });
  /** Решение об увольнении и возврат из архива — только у руководителя. */
  const canDecide = useCan('staff:dismiss');
  /**
   * Какие роли этот человек вправе завести. Администратор — только операторов:
   * то же правило, что и при увольнении. Иначе запрет убирать руководителя
   * обходился бы с другой стороны — заведением второго руководителя себе.
   *
   * Сервер проверяет это сам; здесь мы лишь не показываем кнопки, которые
   * всё равно не сработают.
   */
  const myRole = useAuthStore((state) => state.user?.role);
  const allowedRoles = creatableRoles(myRole ?? 'OPERATOR');

  const load = useCallback(async (): Promise<void> => {
    try {
      setData(await request<StaffResponse>('/api/staff', { method: 'GET' }, 20_000));
      setError(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось загрузить список');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Общая обёртка: сообщение об ошибке показываем рядом, список перечитываем.
   * Действие само возвращает текст о том, что получилось, — исход не всегда
   * известен заранее: увольнение может уйти на согласование, а может пройти сразу.
   */
  const act = async (key: string, action: () => Promise<string>): Promise<void> => {
    setBusy(key);
    setError(null);
    setNotice(null);
    try {
      const done = await action();
      await load();
      setNotice(done);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не получилось');
    } finally {
      setBusy(null);
    }
  };

  // Участки нужны и оператору, и мастеру: одному — где работать, другому —
  // за что отвечать
  const needsSites = SITE_BOUND_ROLES.includes(form.role);

  const submitForm = async (): Promise<void> => {
    await act(
      'create',
      async () => {
        await request<{ employee: EmployeeRow }>(
          '/api/staff',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              lastName: form.lastName.trim(),
              firstName: form.firstName.trim(),
              middleName: form.middleName.trim() === '' ? undefined : form.middleName.trim(),
              phone: form.phone.trim() === '' ? undefined : form.phone.trim(),
              role: form.role,
              login: form.login.trim(),
              password: form.password,
              hiredAt: form.hiredAt,
              siteIds: needsSites ? form.siteIds : [],
            }),
          },
          20_000,
        );
        setForm({ ...EMPTY_FORM, hiredAt: today() });
        setAdding(false);
        return 'Сотрудник заведён';
      },
    );
  };

  const dismiss = async (employee: EmployeeRow): Promise<void> => {
    const reason = window.prompt(
      `Почему увольняется ${employee.fullName}?\n\nДанные останутся в архиве: смены, отчёты и выработка никуда не пропадут.`,
      '',
    );
    if (reason === null || reason.trim().length < 3) return;

    await act(
      employee.id,
      async () => {
        const result = await request<{ status: 'PENDING' | 'APPROVED' }>(
          '/api/staff/dismissal',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userId: employee.id, reason: reason.trim() }),
          },
          20_000,
        );
        return result.status === 'APPROVED'
          ? 'Сотрудник уволен и переведён в архив'
          : 'Отправлено руководителю на согласование';
      },
    );
  };

  const decide = async (employee: EmployeeRow, approved: boolean): Promise<void> => {
    const pending = employee.pendingDismissal;
    if (!pending) return;
    const comment = window.prompt(
      approved ? 'Комментарий к решению (необязательно):' : 'Почему отказываете?',
      '',
    );
    if (comment === null) return;

    await act(
      employee.id,
      async () => {
        await request<{ ok: true }>(
          '/api/staff/dismissal/decide',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: pending.id, approved, comment: comment.trim() || undefined }),
          },
          20_000,
        );
        return approved ? 'Увольнение согласовано' : 'В увольнении отказано';
      },
    );
  };

  const restore = async (employee: EmployeeRow): Promise<void> => {
    if (!window.confirm(`Вернуть ${employee.fullName} на работу? Вход снова заработает.`)) return;
    await act(
      employee.id,
      async () => {
        await request<{ employee: EmployeeRow }>(
          '/api/staff/restore',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: employee.id }),
          },
          20_000,
        );
        return 'Сотрудник снова работает. Пароль прежний — если человека долго не было, смените его';
      },
    );
  };

  const changePassword = async (employee: EmployeeRow): Promise<void> => {
    const password = window.prompt(`Новый пароль для ${employee.fullName} (от 8 символов):`, '');
    if (password === null || password === '') return;
    await act(
      employee.id,
      async () => {
        await request<{ ok: true }>(
          '/api/staff/password',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: employee.id, password }),
          },
          20_000,
        );
        return 'Пароль изменён, сотруднику нужно войти заново';
      },
    );
  };

  const changeHiredAt = async (employee: EmployeeRow): Promise<void> => {
    const hiredAt = window.prompt(
      `С какого числа работает ${employee.fullName}? Формат ГГГГ-ММ-ДД:`,
      employee.hiredAt ?? today(),
    );
    if (hiredAt === null || hiredAt.trim() === '') return;
    await act(
      employee.id,
      async () => {
        await request<{ employee: EmployeeRow }>(
          '/api/staff',
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: employee.id, hiredAt: hiredAt.trim() }),
          },
          20_000,
        );
        return 'Дата выхода изменена';
      },
    );
  };

  const saveName = async (employee: EmployeeRow): Promise<void> => {
    await act(
      employee.id,
      async () => {
        await request<{ employee: EmployeeRow }>(
          '/api/staff',
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: employee.id,
              lastName: draftName.lastName.trim(),
              firstName: draftName.firstName.trim(),
              middleName: draftName.middleName.trim(),
            }),
          },
          20_000,
        );
        setEditingName(null);
        return 'Имя изменено';
      },
    );
  };

  const saveSites = async (employee: EmployeeRow): Promise<void> => {
    await act(
      employee.id,
      async () => {
        await request<{ employee: EmployeeRow }>(
          '/api/staff',
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: employee.id, siteIds: draftSites }),
          },
          20_000,
        );
        setEditingSites(null);
        return 'Допуски сохранены';
      },
    );
  };

  const working = (data?.employees ?? []).filter((employee) => employee.isActive);
  const dismissed = (data?.employees ?? []).filter((employee) => !employee.isActive);

  const siteName = (id: string): string =>
    data?.sites.find((site) => site.id === id)?.name ?? 'участок удалён';

  const checkbox = (checked: boolean, label: string, onChange: () => void): JSX.Element => (
    <label
      key={label}
      className="flex items-center gap-3 rounded-xl bg-muted px-4 py-3 text-base"
    >
      <input type="checkbox" checked={checked} onChange={onChange} className="h-5 w-5" />
      {label}
    </label>
  );

  return (
    <div className="min-h-screen px-4 py-6">
      <header className="mb-6 flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">Сотрудники</h1>
        <Link to="/" className="rounded-xl bg-muted px-4 py-3 text-base font-semibold">
          Назад
        </Link>
      </header>

      {error && <p className="mb-4 rounded-xl bg-rose-50 text-rose-800 ring-1 ring-rose-200 px-4 py-3 text-base">{error}</p>}
      {notice && (
        <p className="mb-4 rounded-xl bg-muted px-4 py-3 text-base text-body">{notice}</p>
      )}

      {!adding && (
        <button
          type="button"
          onClick={() => {
            setAdding(true);
            setNotice(null);
          }}
          className="mb-6 w-full rounded-xl bg-card shadow-card px-5 py-4 text-lg font-semibold active:bg-line"
        >
          Добавить сотрудника
        </button>
      )}

      {adding && (
        <section className="mb-6 rounded-xl bg-card shadow-card p-4">
          <h2 className="mb-4 text-lg font-semibold">Новый сотрудник</h2>

          <div className="flex flex-col gap-3">
            {/* Раздельные поля, а не одна строка: иначе порядок слов зависит
                от того, кто заводил, и список сортируется то по фамилиям,
                то по именам — вперемешку */}
            <input
              value={form.lastName}
              onChange={(event) => setForm({ ...form, lastName: event.target.value })}
              placeholder="Фамилия"
              className="rounded-xl bg-muted px-4 py-3 text-base"
            />
            <input
              value={form.firstName}
              onChange={(event) => setForm({ ...form, firstName: event.target.value })}
              placeholder="Имя"
              className="rounded-xl bg-muted px-4 py-3 text-base"
            />
            <input
              value={form.middleName}
              onChange={(event) => setForm({ ...form, middleName: event.target.value })}
              placeholder="Отчество (необязательно)"
              className="rounded-xl bg-muted px-4 py-3 text-base"
            />
            <input
              value={form.phone}
              onChange={(event) => setForm({ ...form, phone: event.target.value })}
              placeholder="Телефон (необязательно)"
              inputMode="tel"
              className="rounded-xl bg-muted px-4 py-3 text-base"
            />

            <div className="grid grid-cols-2 gap-2">
              {allowedRoles.map((role) => (
                <button
                  key={role}
                  type="button"
                  onClick={() => setForm({ ...form, role })}
                  className={`rounded-xl px-4 py-3 text-base font-semibold ${
                    form.role === role ? 'bg-sky-700' : 'bg-muted'
                  }`}
                >
                  {ROLE_NAMES[role]}
                </button>
              ))}
            </div>

            <input
              value={form.login}
              onChange={(event) => setForm({ ...form, login: event.target.value })}
              placeholder="Логин латиницей"
              autoCapitalize="none"
              autoCorrect="off"
              className="rounded-xl bg-muted px-4 py-3 text-base"
            />
            <input
              value={form.password}
              onChange={(event) => setForm({ ...form, password: event.target.value })}
              placeholder="Пароль, от 8 символов"
              type="text"
              className="rounded-xl bg-muted px-4 py-3 text-base"
            />

            <label className="text-base text-dim">
              Работает с какого числа
              <input
                value={form.hiredAt}
                onChange={(event) => setForm({ ...form, hiredAt: event.target.value })}
                type="date"
                className="mt-1 w-full rounded-xl bg-muted px-4 py-3 text-base text-strong"
              />
            </label>
            <p className="text-base text-faint">
              Дату можно поставить прошлую: от неё считается выслуга и надбавки за неё.
            </p>

            {needsSites && (
              <>
                <p className="text-base text-dim">Допуск к участкам:</p>
                {(data?.sites ?? []).map((site) =>
                  checkbox(form.siteIds.includes(site.id), site.name, () =>
                    setForm({
                      ...form,
                      siteIds: form.siteIds.includes(site.id)
                        ? form.siteIds.filter((id) => id !== site.id)
                        : [...form.siteIds, site.id],
                    }),
                  ),
                )}
              </>
            )}
          </div>

          <div className="mt-4 flex gap-3">
            <button
              type="button"
              disabled={busy === 'create'}
              onClick={() => void submitForm()}
              className="flex-1 rounded-xl bg-sky-700 px-5 py-4 text-lg font-semibold disabled:opacity-50"
            >
              {busy === 'create' ? 'Сохраняю…' : 'Завести'}
            </button>
            <button
              type="button"
              onClick={() => {
                setAdding(false);
                setForm({ ...EMPTY_FORM, hiredAt: today() });
              }}
              className="rounded-xl bg-muted px-5 py-4 text-lg font-semibold"
            >
              Отмена
            </button>
          </div>
        </section>
      )}

      {data === null && !error && <p className="text-base text-dim">Загружаю…</p>}

      <h2 className="mb-3 text-lg font-semibold">Работают</h2>
      <div className="mb-8 flex flex-col gap-3">
        {working.map((employee) => (
          <article key={employee.id} className="rounded-xl bg-card shadow-card p-4">
            <p className="text-lg font-semibold">{employee.fullName}</p>
            <p className="text-base text-dim">
              {ROLE_NAMES[employee.role]}
              {employee.login ? ` · ${employee.login}` : ' · логина нет, войти не сможет'}
              {employee.phone ? ` · ${employee.phone}` : ''}
            </p>
            <p className="mt-1 text-base text-dim">
              {employee.hiredAt ? `Работает с ${employee.hiredAt}` : 'Дата выхода не указана'}
              {SITE_BOUND_ROLES.includes(employee.role) &&
                (employee.siteIds.length === 0
                  ? ' · допусков нет, в график не поставить'
                  : ` · ${employee.siteIds.map(siteName).join(', ')}`)}
            </p>

            {employee.pendingDismissal && (
              <div className="mt-3 rounded-xl bg-amber-50 text-amber-900 ring-1 ring-amber-200 p-3">
                <p className="text-base">
                  Предложено уволить: {employee.pendingDismissal.reason}
                </p>
                <p className="mt-1 text-base text-body">
                  {employee.pendingDismissal.requestedBy},{' '}
                  {new Date(employee.pendingDismissal.requestedAt).toLocaleDateString('ru-RU')}
                </p>
                {canDecide ? (
                  <div className="mt-3 flex gap-2">
                    <button
                      type="button"
                      disabled={busy === employee.id}
                      onClick={() => void decide(employee, true)}
                      className="rounded-xl bg-red-800 px-4 py-3 text-base font-semibold disabled:opacity-50"
                    >
                      Уволить
                    </button>
                    <button
                      type="button"
                      disabled={busy === employee.id}
                      onClick={() => void decide(employee, false)}
                      className="rounded-xl bg-muted px-4 py-3 text-base font-semibold disabled:opacity-50"
                    >
                      Отказать
                    </button>
                  </div>
                ) : (
                  <p className="mt-2 text-base text-amber-700">Ждёт решения руководителя</p>
                )}
              </div>
            )}

            {editingName === employee.id ? (
              <div className="mt-3 flex flex-col gap-2">
                <input
                  value={draftName.lastName}
                  onChange={(event) =>
                    setDraftName({ ...draftName, lastName: event.target.value })
                  }
                  placeholder="Фамилия"
                  className="rounded-xl bg-muted px-4 py-3 text-base"
                />
                <input
                  value={draftName.firstName}
                  onChange={(event) =>
                    setDraftName({ ...draftName, firstName: event.target.value })
                  }
                  placeholder="Имя"
                  className="rounded-xl bg-muted px-4 py-3 text-base"
                />
                <input
                  value={draftName.middleName}
                  onChange={(event) =>
                    setDraftName({ ...draftName, middleName: event.target.value })
                  }
                  placeholder="Отчество (необязательно)"
                  className="rounded-xl bg-muted px-4 py-3 text-base"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={busy === employee.id}
                    onClick={() => void saveName(employee)}
                    className="flex-1 rounded-xl bg-sky-700 px-4 py-3 text-base font-semibold disabled:opacity-50"
                  >
                    Сохранить имя
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingName(null)}
                    className="rounded-xl bg-muted px-4 py-3 text-base font-semibold"
                  >
                    Отмена
                  </button>
                </div>
              </div>
            ) : editingSites === employee.id ? (
              <div className="mt-3 flex flex-col gap-2">
                {(data?.sites ?? []).map((site) =>
                  checkbox(draftSites.includes(site.id), site.name, () =>
                    setDraftSites(
                      draftSites.includes(site.id)
                        ? draftSites.filter((id) => id !== site.id)
                        : [...draftSites, site.id],
                    ),
                  ),
                )}
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={busy === employee.id}
                    onClick={() => void saveSites(employee)}
                    className="flex-1 rounded-xl bg-sky-700 px-4 py-3 text-base font-semibold disabled:opacity-50"
                  >
                    Сохранить допуски
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingSites(null)}
                    className="rounded-xl bg-muted px-4 py-3 text-base font-semibold"
                  >
                    Отмена
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setEditingName(employee.id);
                    setDraftName({
                      lastName: employee.lastName,
                      firstName: employee.firstName,
                      middleName: employee.middleName ?? '',
                    });
                  }}
                  className="rounded-xl bg-muted px-4 py-3 text-base font-semibold"
                >
                  ФИО
                </button>
                <button
                  type="button"
                  disabled={busy === employee.id}
                  onClick={() => void changePassword(employee)}
                  className="rounded-xl bg-muted px-4 py-3 text-base font-semibold disabled:opacity-50"
                >
                  Сменить пароль
                </button>
                <button
                  type="button"
                  disabled={busy === employee.id}
                  onClick={() => void changeHiredAt(employee)}
                  className="rounded-xl bg-muted px-4 py-3 text-base font-semibold disabled:opacity-50"
                >
                  Дата выхода
                </button>
                {SITE_BOUND_ROLES.includes(employee.role) && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingSites(employee.id);
                      setDraftSites(employee.siteIds);
                    }}
                    className="rounded-xl bg-muted px-4 py-3 text-base font-semibold"
                  >
                    Участки
                  </button>
                )}
                {/* Администратор увольняет только операторов. HR, руководителя
                    и других администраторов — исключительно руководитель,
                    поэтому у администратора этой кнопки просто нет. */}
                {!employee.pendingDismissal &&
                  (canDecide || employee.role === 'OPERATOR') && (
                    <button
                      type="button"
                      disabled={busy === employee.id}
                      onClick={() => void dismiss(employee)}
                      className="rounded-xl bg-rose-50 text-rose-800 ring-1 ring-rose-200 px-4 py-3 text-base font-semibold disabled:opacity-50"
                    >
                      Уволить
                    </button>
                  )}
              </div>
            )}
          </article>
        ))}
      </div>

      <h2 className="mb-2 text-lg font-semibold">Архив</h2>
      <p className="mb-3 text-base text-dim">
        Уволенные. Их смены, отчёты и выработка остаются в системе — по ним
        считается статистика за прошлые периоды, а человека можно вернуть.
      </p>
      <div className="flex flex-col gap-3">
        {dismissed.length === 0 && (
          <p className="rounded-xl bg-card shadow-card p-4 text-base text-dim">Пока пусто.</p>
        )}
        {dismissed.map((employee) => (
          <article key={employee.id} className="rounded-xl bg-canvas p-4">
            <p className="text-lg font-semibold text-body">{employee.fullName}</p>
            <p className="text-base text-faint">
              {ROLE_NAMES[employee.role]}
              {employee.login ? ` · ${employee.login}` : ''}
            </p>
            <p className="mt-1 text-base text-faint">
              {employee.hiredAt ?? '—'} → {employee.dismissedAt ?? '—'}
              {` · смен: ${employee.shiftsCount}, отчётов: ${employee.reportsCount}`}
            </p>
            {employee.dismissalReason && (
              <p className="mt-1 text-base text-faint">
                Причина: {employee.dismissalReason}
              </p>
            )}
            {canDecide && (
              <button
                type="button"
                disabled={busy === employee.id}
                onClick={() => void restore(employee)}
                className="mt-3 rounded-xl bg-muted px-4 py-3 text-base font-semibold disabled:opacity-50"
              >
                Вернуть на работу
              </button>
            )}
          </article>
        ))}
      </div>
    </div>
  );
}
